<script lang="ts" setup>
import { InformationCircleIcon } from '@heroicons/vue/24/outline'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import SelectionList from '@/Components/App/Global/SelectionList.vue'
import { PlaceholderReplacements } from '@/Support/Common/Functions/PlaceholdersReplacer'
import { closeButtonModal } from '@/Support/Styles/Global/ModalStyles'
import { SimpleItemsListData } from '@/Support/Common/Interfaces/SimpleItemData'
import LoadingIcon from '@/Components/App/Global/LoadingIcon.vue'
import { computed, ref } from 'vue'
import { t } from '@/Support/lang'
import { RoutingParams } from '@/Support/Routing/Interfaces/routing'
import ErrorField from '@/Components/App/Forms/ErrorField.vue'
import { ModelName } from '@/Support/Global/Interfaces/ModelName'
import AppIcon from '@/Components/App/Global/AppIcon.vue'

interface Props {
  idsSelected: string[] | string | null
  icon?: unknown
  appIcon?: ModelName
  iconName?: string
  route?: string | null
  routeParams?: RoutingParams
  open: boolean
  title: string
  searchPlaceholder?: string
  emptyTitle?: string
  emptyTitleReplacements?: PlaceholderReplacements
  emptyIcon?: unknown
  multiple?: boolean
  idLoading?: string | null
  closeButton?: boolean
  loading?: boolean
  dialogPanelClass?: string
  titleItemClass?: string
  description?: string | null
  buttonTitle?: string | null
  showName?: boolean
  showDescription?: boolean
  defaultItemsData?: SimpleItemsListData | null
  hideSearch?: boolean
  error?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  multiple: false,
  closeButton: true,
  dialogPanelClass: 'sm:max-w-sm',
  showName: true,
  showDescription: true,
})

const emit = defineEmits<{
  (e: 'selected', value: string): void
  (e: 'close'): void
  (e: 'all-selected', value: string[]): void
  (e: 'itemsLoaded', value: SimpleItemsListData): void
}>()

const originalIdsSelected = props.idsSelected

const selectionList = ref<InstanceType<typeof SelectionList> | null>(null)
const loadItems = async () => {
  await selectionList.value?.loadItems()
}
defineExpose({
  loadItems,
})
const selected = (id: string) => {
  emit('selected', id)
}

const allSelected = ref<string[]>([])

const updateAllSelected = (ids: string[]) => {
  allSelected.value = ids
  emit('all-selected', ids)
}

const buttonTitleDefault = computed(() =>
  originalIdsSelected !== props.idsSelected ? 'To update' : 'Close',
)
</script>
<template>
  <div class="flex items-center justify-center mr-2">
    <TransitionRoot as="template" :show="open">
      <Dialog as="div" class="relative z-10" @close="emit('close')">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div
            class="fixed inset-0 backdrop-blur-xs bg-neutral-500/25 transition-opacity"
          />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div
            class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
          >
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                class="relative transform overflow-hidden rounded-lg bg-white dark:bg-neutral-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 w-full sm:p-6"
                :class="dialogPanelClass"
              >
                <div>
                  <slot name="icon">
                    <div
                      class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100"
                    >
                      <AppIcon
                        v-if="appIcon"
                        :icon="appIcon"
                        class="h-6 w-6 text-neutral-600"
                      />
                      <component
                        v-else-if="icon"
                        :is="icon"
                        :name="iconName"
                        class="h-6 w-6 text-neutral-600"
                      />
                      <InformationCircleIcon
                        v-else
                        class="h-6 w-6 text-neutral-600"
                      />
                    </div>
                  </slot>

                  <div class="mt-3 text-center sm:mt-5">
                    <DialogTitle
                      as="h3"
                      class="text-base font-semibold leading-6 text-neutral-900 dark:text-neutral-200 flex items-center justify-center"
                    >
                      {{ __(title) }}
                      <LoadingIcon :loading="loading" />
                    </DialogTitle>
                    <p
                      v-if="description"
                      class="text-sm font-light text-neutral-700 dark:text-neutral-300"
                    >
                      {{ __(description) }}
                    </p>
                    <ErrorField
                      v-if="error"
                      flashy
                      class="mt-4"
                      :message="error"
                    />
                    <SelectionList
                      ref="selectionList"
                      :ids-selected="idsSelected"
                      :search-placeholder="searchPlaceholder"
                      :route="route"
                      class="mt-4"
                      :multiple="multiple"
                      :id-loading="idLoading"
                      :title-item-class="titleItemClass"
                      :additional-params="routeParams"
                      :show-name="showName"
                      :show-description="showDescription"
                      :default-items-data="defaultItemsData"
                      :hide-search="hideSearch"
                      :empty-icon="emptyIcon"
                      :empty-title="emptyTitle"
                      @selected="selected"
                      @all-selected="updateAllSelected"
                      @items-loaded="emit('itemsLoaded', $event)"
                    />
                  </div>
                  <div class="mt-5 sm:mt-6">
                    <button
                      v-if="closeButton"
                      type="button"
                      :class="closeButtonModal"
                      @click="emit('close', allSelected)"
                    >
                      {{ t(buttonTitle || buttonTitleDefault) }}
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>
